import { calculateHeaderGroupHeight } from '@theme/critical';
import { Component } from '@theme/component';
import { onDocumentLoaded, changeMetaThemeColor } from '@theme/utilities';

/**
 * @typedef {Object} HeaderComponentRefs
 * @property {HTMLDivElement} headerDrawerContainer - The header drawer container element
 * @property {HTMLElement} headerMenu - The header menu element
 * @property {HTMLElement} headerRowTop - The header top row element
 */

/**
 * @typedef {CustomEvent<{ minimumReached: boolean }>} OverflowMinimumEvent
 */

/**
 * A custom element that manages the site header.
 *
 * @extends {Component<HeaderComponentRefs>}
 */

class HeaderComponent extends Component {
  requiredRefs = ['headerRowTop'];

  /**
   * Width of window when header drawer was hidden
   * @type {number | null}
   */
  #menuDrawerHiddenWidth = null;

  /**
   * An intersection observer for monitoring sticky header position
   * @type {IntersectionObserver | null}
   */
  #intersectionObserver = null;

  /**
   * Whether the header has been scrolled offscreen, when sticky behavior is 'scroll-up'
   * @type {boolean}
   */
  #offscreen = false;

  /**
   * The last recorded scrollTop of the document, when sticky behavior is 'scroll-up
   * @type {number}
   */
  #lastScrollTop = 0;

  /**
   * A timeout to allow for hiding animation, when sticky behavior is 'scroll-up'
   * @type {number | null}
   */
  #timeout = null;

  /**
   * Current scroll direction state to prevent rapid direction changes
   * @type {'up' | 'down' | 'none'}
   */
  #currentDirection = 'none';

  /**
   * Flag to track if announcement bar is currently visible
   * @type {boolean}
   */
  #announcementBarVisible = true;

  /**
   * The duration to wait for hiding animation, when sticky behavior is 'scroll-up'
   * @constant {number}
   */
  #animationDelay = 150;

  /**
   * Reference to the announcement bar element
   * @type {HTMLElement | null}
   */
  #announcementBar = null;

  /**
   * Keeps the global `--header-height` custom property up to date,
   * which other theme components can then consume
   */
  #resizeObserver = new ResizeObserver(([entry]) => {
    if (!entry) return;

    const { height } = entry.target.getBoundingClientRect();
    document.body.style.setProperty('--header-height', `${height}px`);

    // Check if the menu drawer should be hidden in favor of the header menu
    if (this.#menuDrawerHiddenWidth && window.innerWidth > this.#menuDrawerHiddenWidth) {
      this.#updateMenuVisibility(false);
    }
  });

  /**
   * Observes the header while scrolling the viewport to track when its actively sticky
   * @param {Boolean} alwaysSticky - Determines if we need to observe when the header is offscreen
   */
  #observeStickyPosition = (alwaysSticky = true) => {
    if (this.#intersectionObserver) return;

    const config = {
      threshold: alwaysSticky ? 1 : 0,
    };

    this.#intersectionObserver = new IntersectionObserver(([entry]) => {
      if (!entry) return;

      const { isIntersecting } = entry;

      if (alwaysSticky) {
        this.dataset.stickyState = isIntersecting ? 'inactive' : 'active';
        changeMetaThemeColor(this.refs.headerRowTop);
      } else {
        this.#offscreen = !isIntersecting || this.dataset.stickyState === 'active';
      }
    }, config);

    this.#intersectionObserver.observe(this);
  };

  /**
   * Handles the overflow minimum event from the header menu
   * @param {OverflowMinimumEvent} event
   */
  #handleOverflowMinimum = (event) => {
    this.#updateMenuVisibility(event.detail.minimumReached);
  };

  /**
   * Updates the visibility of the menu and drawer
   * @param {boolean} hideMenu - Whether to hide the menu and show the drawer
   */
  #updateMenuVisibility(hideMenu) {
    if (!this.refs.headerDrawerContainer || !this.refs.headerMenu) return;
    
    if (hideMenu) {
      this.refs.headerDrawerContainer.classList.remove('desktop:hidden');
      this.#menuDrawerHiddenWidth = window.innerWidth;
      this.refs.headerMenu.classList.add('hidden');
    } else {
      this.refs.headerDrawerContainer.classList.add('desktop:hidden');
      this.#menuDrawerHiddenWidth = null;
      this.refs.headerMenu.classList.remove('hidden');
    }
  }

  /**
   * Update body padding for fixed header (both mobile and desktop)
   */
  #updateBodyPadding() {
    const headerGroup = document.getElementById('header-group');
    
    if (headerGroup) {
      // Only calculate height of the actual header component, not mega menus
      const headerComponent = headerGroup.querySelector('header-component');
      const announcementBar = headerGroup.querySelector('.announcement-bar');
      
      let headerHeight = 0;
      
      // Add announcement bar height if visible
      if (announcementBar && this.#announcementBarVisible) {
        headerHeight += announcementBar.getBoundingClientRect().height;
      }
      
      // Add main header height
      if (headerComponent) {
        headerHeight += headerComponent.getBoundingClientRect().height;
      }
      
      // Fallback to full header group if no component found
      if (headerHeight === 0) {
        headerHeight = headerGroup.getBoundingClientRect().height;
      }
      
      document.body.style.paddingTop = `${headerHeight}px`;
      // Also update --header-group-height for mega menu positioning
      document.body.style.setProperty('--header-group-height', `${headerHeight}px`);
    }
  }

  /**
   * Enable smooth transition for body padding
   */
  #enableBodyPaddingTransition() {
    if (!document.body.style.transition.includes('padding-top')) {
      document.body.style.transition = 'padding-top 0.3s ease-in-out';
    }
  }

  /**
   * Show the announcement bar
   */
  #showAnnouncementBar() {
    if (this.#announcementBar && !this.#announcementBarVisible) {
      this.#announcementBarVisible = true;
      this.#announcementBar.style.transform = 'translateY(0)';
      this.#announcementBar.style.opacity = '1';
      this.#announcementBar.style.marginBottom = '0';
      
      // Enable smooth body padding transition and update after transition completes
      this.#enableBodyPaddingTransition();
      setTimeout(() => this.#updateBodyPadding(), 300);
    }
  }

  /**
   * Hide the announcement bar
   */
  #hideAnnouncementBar() {
    if (this.#announcementBar && this.#announcementBarVisible) {
      this.#announcementBarVisible = false;
      const announcementHeight = this.#announcementBar.getBoundingClientRect().height;
      this.#announcementBar.style.transform = `translateY(-${announcementHeight}px)`;
      this.#announcementBar.style.opacity = '0';
      // Use negative margin to remove the space it takes up
      this.#announcementBar.style.marginBottom = `-${announcementHeight}px`;
      
      // Enable smooth body padding transition and update after transition completes
      this.#enableBodyPaddingTransition();
      setTimeout(() => this.#updateBodyPadding(), 300);
    }
  }

  #handleWindowScroll = () => {
    const stickyMode = this.getAttribute('sticky');
    if (!this.#offscreen && stickyMode !== 'always') return;

    const scrollTop = document.scrollingElement?.scrollTop ?? 0;
    const scrollDifference = scrollTop - this.#lastScrollTop;
    
    if (this.#timeout) {
      clearTimeout(this.#timeout);
      this.#timeout = null;
    }

    if (stickyMode === 'always') {
      // Calculate 5% of page height
      const pageHeight = document.documentElement.scrollHeight - window.innerHeight;
      const topFivePercent = pageHeight * 0.02;
      const isInTopFivePercent = scrollTop <= topFivePercent;

      // Always show announcement bar in top 5% of page
      if (isInTopFivePercent) {
        if (this.#currentDirection !== 'none') {
          this.#currentDirection = 'none';
          this.dataset.scrollDirection = 'none';
        }
        this.#showAnnouncementBar();
        this.#lastScrollTop = scrollTop;
      } else {
        // Use hysteresis: different thresholds based on current state
        // This prevents rapid toggling when hovering around the threshold
        const scrollThreshold = this.#announcementBarVisible ? 15 : 10;
        
        // Only update if scroll difference exceeds threshold
        if (Math.abs(scrollDifference) > scrollThreshold) {
          const isScrollingUp = scrollDifference < 0;
          const newDirection = isScrollingUp ? 'up' : 'down';
          
          // Only change direction if it's actually different
          if (this.#currentDirection !== newDirection) {
            this.#currentDirection = newDirection;
            this.dataset.scrollDirection = newDirection;
            
            if (isScrollingUp) {
              this.#showAnnouncementBar();
            } else {
              this.#hideAnnouncementBar();
            }
          }
          
          this.#lastScrollTop = scrollTop;
        }
      }
      
      return;
    }

    const isScrollingUp = scrollDifference < 0;

    if (isScrollingUp) {
      this.removeAttribute('data-animating');

      if (this.getBoundingClientRect().top >= 0) {
        // reset sticky state when header is scrolled up to natural position
        this.#offscreen = false;
        this.dataset.stickyState = 'inactive';
        this.dataset.scrollDirection = 'none';
      } else {
        // show sticky header when scrolling up
        this.dataset.stickyState = 'active';
        this.dataset.scrollDirection = 'up';
      }
    } else if (this.dataset.stickyState === 'active') {
      this.dataset.scrollDirection = 'none';
      // delay transitioning to idle hidden state for hiding animation
      this.setAttribute('data-animating', '');

      this.#timeout = setTimeout(() => {
        this.dataset.stickyState = 'idle';
        this.removeAttribute('data-animating');
      }, this.#animationDelay);
    } else {
      this.dataset.scrollDirection = 'none';
      this.dataset.stickyState = 'idle';
    }

    this.#lastScrollTop = scrollTop;
  };

  connectedCallback() {
    super.connectedCallback();
    this.#resizeObserver.observe(this);
    this.addEventListener('overflowMinimum', this.#handleOverflowMinimum);

    // Find announcement bar in the header group
    const headerGroup = document.querySelector('#header-group');
    if (headerGroup) {
      this.#announcementBar = headerGroup.querySelector('.announcement-bar');
      
      // Add transition styles to announcement bar if found
      if (this.#announcementBar) {
        this.#announcementBar.style.transition = 'transform 0.3s ease-in-out, opacity 0.3s ease-in-out, margin-bottom 0.3s ease-in-out';
      }
    }

    const stickyMode = this.getAttribute('sticky');
    if (stickyMode) {
      this.#observeStickyPosition(stickyMode === 'always');

      if (stickyMode === 'scroll-up' || stickyMode === 'always') {
        // Initialize last scroll position to current position to prevent initial jump
        this.#lastScrollTop = document.scrollingElement?.scrollTop ?? 0;
        
        // Add scroll listener with passive flag for better mobile performance
        document.addEventListener('scroll', this.#handleWindowScroll, { passive: true });
        
        // Also handle touchmove for mobile to ensure smooth behavior
        document.addEventListener('touchmove', this.#handleWindowScroll, { passive: true });
        
        // Handle window resize to update body padding on mobile
        window.addEventListener('resize', () => this.#updateBodyPadding());
        
        // Initial body padding setup for mobile
        this.#updateBodyPadding();
      }
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
    this.#intersectionObserver?.disconnect();
    this.removeEventListener('overflowMinimum', this.#handleOverflowMinimum);
    document.removeEventListener('scroll', this.#handleWindowScroll);
    document.removeEventListener('touchmove', this.#handleWindowScroll);
    document.body.style.setProperty('--header-height', '0px');
  }
}

if (!customElements.get('header-component')) {
  customElements.define('header-component', HeaderComponent);
}

onDocumentLoaded(() => {
  const header = document.querySelector('#header-component');
  const headerGroup = document.querySelector('#header-group');

  // Update header group height on resize of any child
  if (headerGroup) {
    const resizeObserver = new ResizeObserver(() => calculateHeaderGroupHeight(header, headerGroup));

    // Observe all children of the header group
    const children = headerGroup.children;
    for (let i = 0; i < children.length; i++) {
      const element = children[i];
      if (element === header || !(element instanceof HTMLElement)) continue;
      resizeObserver.observe(element);
    }

    // Also observe the header group itself for child changes
    const mutationObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          // Re-observe all children when the list changes
          const children = headerGroup.children;
          for (let i = 0; i < children.length; i++) {
            const element = children[i];
            if (element === header || !(element instanceof HTMLElement)) continue;
            resizeObserver.observe(element);
          }
        }
      }
    });

    mutationObserver.observe(headerGroup, { childList: true });
  }
});
